import clsx from "clsx";
import sample from "lodash/sample";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { Seomatic } from "react-seomatic";

import ColumnLayout from "@components/ColumnLayout";
import ContentSections from "@components/ContentSections";
import GlossaryEntryHeader from "@components/GlossaryEntryHeader";
import PageSections from "@components/PageSections";
import { getEntryCategoryArray } from "@lib/getEntryCategoryArray";

import { PLASMIC } from "@abnormal/plasmic-init";
import GlossaryLayoutNewsletterForm from "@components/GlossaryLayoutNewsletterForm";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import {
  ComponentRenderData,
  extractPlasmicQueryData,
  PlasmicComponent,
  PlasmicRootProvider
} from "@plasmicapp/loader-nextjs";
import QueryGlossary from "@queries/glossary/QueryGlossary.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";

import { headerHeight } from "@lib/headerHeight";
import { useAtomValue } from "jotai";
import Script from "next/script";

export const getStaticPaths: GetStaticPaths = async (context) => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, {
		section: "glossary",
		limit: 20
	});

	const paths = [];

	response.entries.map((item) => {
		console.log(`Generating /glossary/${item.slug}`);

		paths.push({
			params: { slug: item.slug }
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
	const uri = `/glossary/${slug}`;

	const plasmicData = await PLASMIC.maybeFetchComponentData([
		"BladesDynamicDemoBlade",
		// "RelatedContentItem",
		"EntriesSidebarVerticalList",
		"Footer"
  ], {
    // deferChunks: true,
  });
	if (!plasmicData) {
		throw new Error("No Plasmic design found");
	}

	// Fetch the page's data
	const response = await craft(
		QueryGlossary,
		{ slug },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		resources,
		item,
		items,
		primaryNavigation,
		primaryCallToActions,
		persistentNav
	} = response;

	if (!item) {
		return {
			notFound: true
		};
	}

	const queryCache = await extractPlasmicQueryData(
		<PlasmicRootProvider loader={PLASMIC} prefetchedData={plasmicData}>
			<PlasmicComponent
				component="EntriesCardResources"
				projectId={process.env.NEXT_PUBLIC_PLASMIC_DESIGN_SYSTEM_PROJECT_ID}
			/>
			<PlasmicComponent component="BladesDynamicDemoBlade" />
			<PlasmicComponent
				component="EntriesSidebarVerticalList"
				projectId={process.env.NEXT_PUBLIC_PLASMIC_DESIGN_SYSTEM_PROJECT_ID}
			/>
		</PlasmicRootProvider>
	);

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions
			},
			pageEntry,
			persistentNav,
			resources,
			item,
			items,
			plasmicData,
			queryCache
		},
		!item,
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	resources: any;
	featuredEntries: any;
	item: any;
	items: any;
	plasmicData: ComponentRenderData;
	queryCache: Record<string, any>;
}

export default function Page({
	pageEntry,
	item,
	items,
	resources,
	plasmicData,
	queryCache
}: IProps): JSX.Element {
	const router = useRouter();
	useSetHeaderTheme(pageEntry.headerTheme);

	// Select just one call to action at random

	let formData;

	const heroImage = !!item.heroImage ? item.heroImage[0] : null;
	const heroImageBlur = !!item.heroImageBlur ? item.heroImageBlur[0] : null;
	const mobileHeroImage = !!item.mobileHeroImage
		? item.mobileHeroImage[0]
		: null;
	const mobileHeroImageBlur = !!item.mobileHeroImageBlur
		? item.mobileHeroImageBlur[0]
		: null;

	let faqs = [];
	let faqSchema = {};

	if (item.faqSchema?.length) {
		item.faqSchema.map((item) => {
			faqs.push({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: {
					"@type": "Answer",
					text: item.answer
				}
			});
		});

		faqSchema = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faqs
		};
	}

	return (
		<PlasmicRootProvider
			loader={PLASMIC}
			prefetchedData={plasmicData}
			prefetchedQueryData={queryCache}
			pageQuery={router.query}
		>
			<Seomatic Head={Head} {...item.seomatic} />

			{item.faqSchema?.length > 0 && (
				<Script
					type="application/ld+json"
					id="faqSchema"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
				/>
			)}

			<GlossaryEntryHeader
				heading={item.heading ? item.heading : item.title}
				breadcrumb={pageEntry}
				heroImage={heroImage}
				heroImageBlur={heroImageBlur}
				//mobileHeroImage={mobileHeroImage}
				//mobileHeroImageBlur={mobileHeroImageBlur}
				shareAlignment="right"
				overlayTextTheme={item.overlayTextTheme}
				url={item.url}
				summary={item.summary}
				showSummary={item.showSummary}
				target={item.target}
				secondaryTarget={item.secondaryTarget}
				legacyButtonBackgroundColor={item.legacyButtonBackgroundColor}
				buttonTextColor={item.buttonTextColor}
			/>

			<div className={clsx("relative", "pb-15")}>
				<ColumnLayout>
					<ColumnLayout.MainColumn className={clsx("max-w-[735px]")}>
						{!!item.contentSections && (
							<ContentSections sections={item.contentSections} />
						)}
					</ColumnLayout.MainColumn>
					<ColumnLayout.SidebarColumn
						className={clsx(
							"lg:pb-15",
							"px-0",
							"lg:max-w-[310px]",
							"lg:m-auto, sm:m-0"
						)}
					>
						<GlossaryLayoutNewsletterForm
							formHeading={pageEntry.formHeading}
							formDescription={pageEntry.formDescription}
							form={pageEntry?.form?.[0]}
							formData={formData}
							successMessage={pageEntry.successMessage}
						/>
						<PlasmicComponent component="EntriesSidebarVerticalList" />
					</ColumnLayout.SidebarColumn>
				</ColumnLayout>
			</div>

			<PlasmicComponent component="BladesDynamicDemoBlade" />
		</PlasmicRootProvider>
	);
}
